import React from "react";
import KpiTileCard from "./KpiTileCard";
import useViewport from "../../hooks/useViewport";

/**
 * Overview KPI tiles for both dashboards (clinic + standard). Owns *layout only*;
 * the pages keep what each tile means and does (filter, clear, open detail view),
 * passing it in as `active` / `onClick` per tile.
 *
 *   tiles: [{ key, label, icon, value, color, weekKey, active, onClick }]
 *     weekKey "total"   - headline count; click clears the status filter
 *     weekKey "revenue" - headline money; not interactive
 *     weekKey null      - plain stat (utilization); not interactive
 *     anything else     - a status filter
 *
 * Why this exists: the tiles used to be `repeat(7, 1fr)` at every width. On a
 * 390px phone that is 44px per tile - narrower than the card's own padding and
 * icon - so only the icons showed, with no label or number; on an iPad the
 * labels truncated and Revenue read "A...".
 *
 *   phone    headline cards (Total + Revenue), then status filters as tap-to-
 *            filter count chips, then utilization as a quiet line
 *   tablet   the same cards in as many columns as keep each >=150px wide
 *   desktop  one row, unchanged
 */

// Sidebar rail (72) + content padding (2 x 16) - what a tablet's content area loses.
const TABLET_CHROME = 104;
const MIN_TILE = 150;
const GAP = 10;

const tabletColumns = (count, viewportWidth) => {
  const available = viewportWidth - TABLET_CHROME;
  let cols = Math.max(2, Math.min(count, Math.floor((available + GAP) / (MIN_TILE + GAP))));
  // Never strand a single tile on the last row (7 in 6 columns = 6 + 1).
  if (cols > 2 && count % cols === 1) cols -= 1;
  return cols;
};

const HeadlineCard = ({ tile }) => {
  const interactive = typeof tile.onClick === "function";
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={tile.onClick}
      aria-pressed={interactive ? !!tile.active : undefined}
      style={{
        textAlign: "left",
        minWidth: 0,
        padding: "12px 14px",
        borderRadius: "12px",
        border: `1px solid ${tile.active ? tile.color : "#E3EBF8"}`,
        background: tile.active ? `${tile.color}0D` : "#fff",
        cursor: interactive ? "pointer" : "default",
        font: "inherit",
      }}
    >
      <div style={{ fontSize: "12px", fontWeight: 400, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {tile.label}
      </div>
      <div
        style={{
          marginTop: "2px",
          fontSize: "22px",
          fontWeight: 700,
          lineHeight: 1.15,
          color: "#111827",
          overflowWrap: "anywhere",
        }}
      >
        {tile.value ?? "-"}
      </div>
    </Tag>
  );
};

const FilterChip = ({ tile }) => (
  <button
    type="button"
    onClick={tile.onClick}
    aria-pressed={!!tile.active}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      minHeight: "44px",
      padding: "0 14px",
      borderRadius: "999px",
      border: `1px solid ${tile.active ? tile.color : "#E3EBF8"}`,
      background: tile.active ? `${tile.color}14` : "#fff",
      color: "#111827",
      fontSize: "13px",
      cursor: "pointer",
    }}
  >
    <span style={{ width: 8, height: 8, borderRadius: "50%", background: tile.color, flexShrink: 0 }} />
    <span>{tile.label}</span>
    <strong style={{ fontSize: "15px" }}>{tile.value ?? 0}</strong>
  </button>
);

function PhoneSummary({ tiles }) {
  const total = tiles.find((t) => t.weekKey === "total");
  const revenue = tiles.find((t) => t.weekKey === "revenue");
  const stats = tiles.filter((t) => !t.weekKey);
  const filters = tiles.filter((t) => t.weekKey && t.weekKey !== "total" && t.weekKey !== "revenue");
  const headlines = [total, revenue].filter(Boolean);

  return (
    <div className="mb-4 mt-2" data-tour="dashboard-stats">
      {headlines.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${headlines.length}, minmax(0, 1fr))`,
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          {headlines.map((t) => (
            <HeadlineCard key={t.key} tile={t} />
          ))}
        </div>
      )}

      {filters.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {filters.map((t) => (
            <FilterChip key={t.key} tile={t} />
          ))}
        </div>
      )}

      {stats.map((t) => (
        <div
          key={t.key}
          style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", fontSize: "13px", color: "#6B7280" }}
        >
          <span style={{ display: "inline-flex", fontSize: "16px" }}>{t.icon}</span>
          <span>{t.label}</span>
          <strong style={{ color: "#111827" }}>{t.value ?? "-"}</strong>
        </div>
      ))}
    </div>
  );
}

export default function KpiTileGrid({ tiles }) {
  const { isPhone, isDesktop, width } = useViewport();

  if (isPhone) return <PhoneSummary tiles={tiles} />;

  const columns = isDesktop ? tiles.length : tabletColumns(tiles.length, width);

  return (
    <div
      className="mb-4 mt-2"
      data-tour="dashboard-stats"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridAutoRows: "1fr",
        gap: `${GAP}px`,
        alignItems: "stretch",
      }}
    >
      {tiles.map((t) => (
        <div key={t.key} style={{ minWidth: 0, height: "100%" }}>
          <KpiTileCard icon={t.icon} label={t.label} value={t.value} color={t.color} active={t.active} onClick={t.onClick} />
        </div>
      ))}
    </div>
  );
}
